import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  EnvelopeIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
  QuestionMarkCircleIcon,
  BriefcaseIcon,
  NewspaperIcon,
  ShieldCheckIcon,
  BuildingOffice2Icon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In production, this would send to backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
  };

  const contactMethods = [
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'General Inquiries',
      email: 'info@templum.io',
      description: 'Questions about our platform and services'
    },
    {
      icon: QuestionMarkCircleIcon,
      title: 'Support',
      email: 'support@templum.io',
      description: 'Technical assistance and account help'
    },
    {
      icon: BriefcaseIcon,
      title: 'Business & Partnerships',
      email: 'partnerships@nardiha.com',
      description: 'Collaboration and integration opportunities'
    },
    {
      icon: NewspaperIcon,
      title: 'Press & Media',
      email: 'press@nardiha.com',
      description: 'Media inquiries and press releases'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Legal & Compliance',
      email: 'legal@nardiha.com',
      description: 'Legal matters and compliance questions'
    },
    {
      icon: EnvelopeIcon,
      title: 'Careers',
      email: 'careers@nardiha.com',
      description: 'Job applications and inquiries'
    }
  ];

  const socialLinks = [
    { name: 'Twitter', url: 'https://twitter.com/templum_io', handle: '@templum_io' },
    { name: 'Discord', url: 'https://discord.gg/templum', handle: 'discord.gg/templum' },
    { name: 'Telegram', url: 'https://t.me/templum_official', handle: '@templum_official' },
    { name: 'LinkedIn', url: 'https://linkedin.com/company/nardiha', handle: 'NARDIHA Holdings' },
    { name: 'GitHub', url: 'https://github.com/nardiha', handle: '@nardiha' },
  ];

  const offices = [
    {
      city: 'Panama City',
      country: 'Panama',
      type: 'Headquarters',
      address: 'NARDIHA Holdings S.A.\nPanama City, Republic of Panama'
    },
    {
      city: 'Lisbon',
      country: 'Portugal',
      type: 'Tech Hub',
      address: 'Avenida Example, 456\n1000-001 Lisbon, Portugal'
    }
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <EnvelopeIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Get in <span className="text-primary-400">Touch</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Have questions? We'd love to hear from you. Choose the best way to reach us below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-dark-900/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Contact Departments</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {contactMethods.map((method, idx) => {
                const Icon = method.icon;
                return (
                  <motion.a
                    key={idx}
                    href={`mailto:${method.email}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 hover:border-primary-500/50 transition-colors group"
                  >
                    <Icon className="w-8 h-8 text-primary-400 mb-3 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg font-semibold text-white mb-1">{method.title}</h3>
                    <p className="text-primary-400 text-sm mb-2">{method.email}</p>
                    <p className="text-gray-400 text-sm">{method.description}</p>
                  </motion.a>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Social */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl font-bold text-white mb-6">Send Us a Message</h2>
                {submitted ? (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-8 text-center">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">Message Sent!</h3>
                    <p className="text-gray-400">Thank you for reaching out. We'll get back to you within 24-48 hours.</p>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: '', email: '', subject: 'general', message: '' });
                      }}
                      className="mt-4 text-primary-400 hover:text-primary-300"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 bg-dark-800 border border-dark-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 bg-dark-800 border border-dark-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Subject</label>
                      <select
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-4 py-3 bg-dark-800 border border-dark-600 rounded-xl text-white focus:outline-none focus:border-primary-500"
                      >
                        <option value="general">General Inquiry</option>
                        <option value="support">Technical Support</option>
                        <option value="partnership">Business Partnership</option>
                        <option value="press">Press & Media</option>
                        <option value="feedback">Feedback</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                      <textarea
                        required
                        rows={5}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-3 bg-dark-800 border border-dark-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 resize-none"
                        placeholder="How can we help you?"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors font-medium"
                    >
                      Send Message
                    </button>
                  </form>
                )}
              </motion.div>

              {/* Social & Office */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-8"
              >
                {/* Social Links */}
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Connect With Us</h2>
                  <div className="space-y-3">
                    {socialLinks.map((social, idx) => (
                      <a
                        key={idx}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-4 bg-dark-800/50 rounded-xl border border-dark-700 hover:border-primary-500/50 transition-colors"
                      >
                        <span className="text-white font-medium">{social.name}</span>
                        <span className="text-primary-400">{social.handle}</span>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Offices */}
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Our Offices</h2>
                  <div className="space-y-4">
                    {offices.map((office, idx) => (
                      <div
                        key={idx}
                        className="p-6 bg-dark-800/50 rounded-xl border border-dark-700"
                      >
                        <div className="flex items-start gap-4">
                          <div className="p-2 bg-primary-500/20 rounded-lg">
                            <BuildingOffice2Icon className="w-6 h-6 text-primary-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-white">{office.city}</h3>
                              <span className="px-2 py-0.5 bg-dark-700 text-gray-400 text-xs rounded">{office.type}</span>
                            </div>
                            <p className="text-gray-400 text-sm mt-1 whitespace-pre-line">{office.address}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Response Time */}
                <div className="p-6 bg-primary-500/10 border border-primary-500/30 rounded-xl">
                  <h3 className="text-lg font-semibold text-white mb-2">Response Time</h3>
                  <p className="text-gray-300 text-sm">
                    We typically respond to inquiries within 24-48 business hours. For urgent matters, please reach out via our Discord or Telegram communities for faster assistance.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ CTA */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <GlobeAltIcon className="w-12 h-12 text-primary-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">
              Looking for Answers?
            </h2>
            <p className="text-gray-400 mb-6">
              Check out our documentation and FAQ section for quick answers to common questions.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/docs"
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
              >
                View Documentation
              </a>
              <a
                href="/docs#faq"
                className="px-6 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-xl transition-colors"
              >
                Browse FAQ
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
